import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { scaleLinear, scaleOrdinal, schemeCategory10, stack, stackOrderNone, stackOffsetNone, scaleUtc, extent, area, timeFormat } from 'd3';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
function getYAxisData(data, keys, height, margin, yMaxTicks) {
  //calculate the maximum y value we have in our stacked area chart
  var yMax = data.reduce(function (maxSum, d) {
    var sum = Object.entries(d).reduce(function (sum, _a) {
      var key = _a[0],
        value = _a[1];
      //if this value is a number (ie not the date) AND the key is valid
      if (typeof value === "number" && keys.includes(key)) {
        return sum + value; //return the sum plus the value
      }
      return sum; //else ignore the value
    }, 0);
    if (sum > maxSum) {
      //if this sum is higher
      return sum; //return it
    }
    return maxSum; //else ignore the lower sum
  }, 0);
  var yDomain = [0, yMax];
  var yTicks = []; //will hold the numbers to show on the y axis
  var rawIncrement = yDomain[1] / yMaxTicks; //difference between max and min divided by the max number of y ticks
  var digits = Math.log(rawIncrement) * Math.LOG10E + 1 | 0; //get the number digits in this number
  var magnitude = Math.pow(10, digits - 1); //the power of 10 with the same number of digits
  var roundedIncrement = Math.ceil(rawIncrement / magnitude) * magnitude; //round up to the next highest number with trailing zeros (ex 1000, 2000, etc)
  //starting at the min, increment by the roundedIncrement until we pass the max
  var i = 0;
  for (i; i < yDomain[1]; i += roundedIncrement) {
    yTicks.push(i);
  }
  yTicks.push(i); //push an extra rounded increment
  yDomain[1] = i; //set the max to the extra rounded increment
  var yScale = scaleLinear().domain(yDomain).range([height - margin.bottom, margin.top]);
  return {
    yTicks: yTicks,
    yScale: yScale
  };
}
function StackedAreaChart(props) {
  var data = props.data,
    keys = props.keys,
    areaOpacity = props.areaOpacity,
    axisStroke = props.axisStroke,
    axisStrokeWidth = props.axisStrokeWidth,
    propsColorFunction = props.colorFunction,
    dateFormat = props.dateFormat,
    dots = props.dots,
    dotsRadius = props.dotsRadius,
    dotsStroke = props.dotsStroke,
    dotsStrokeWidth = props.dotsStrokeWidth,
    dotsFilter = props.dotsFilter,
    height = props.height,
    margin = props.margin,
    onClickHandler = props.onClickHandler,
    onMouseOverHandler = props.onMouseOverHandler,
    xTickSize = props.xTickSize,
    xTicksSkip = props.xTicksSkip,
    yFormat = props.yFormat,
    yTickSize = props.yTickSize,
    yMaxTicks = props.yMaxTicks;
  var _a = useState(""),
    hoverKey = _a[0],
    setHoverKey = _a[1];
  var _b = useState(500),
    width = _b[0],
    setWidth = _b[1];
  var _c = useState(null),
    xHoverIndex = _c[0],
    setXHoverIndex = _c[1];
  var ref = useRef(null);
  var resize = useCallback(function () {
    if (ref.current) {
      setWidth(ref.current.clientWidth); //responsive chart width
    }
  }, [ref]);
  useEffect(function () {
    window.addEventListener('resize', resize); //add resize listener for responsiveness
    resize(); //initial resize
    return function () {
      window.removeEventListener('resize', resize);
    };
  }, [resize]);
  var mapPositionToIndex = useCallback(function (xValue) {
    var domain = width - margin.left - margin.right;
    var relativeX = xValue - margin.left;
    var proportion = relativeX / domain;
    var value = Math.round((data.length - 1) * proportion);
    return Math.min(Math.max(0, value), data.length - 1);
  }, [data, margin, width]);
  var mouseMove = function (e, key) {
    if (ref.current) {
      var boundings = ref.current.getBoundingClientRect(); //get the bounds of the chart
      //@ts-ignore
      var clientX = e.clientX || e.touches[0].clientX;
      var newXHoverIndex = mapPositionToIndex(clientX - boundings.left); //calculate the x hover index
      if (newXHoverIndex !== xHoverIndex || key !== hoverKey) {
        //if we should set a new xHoverIndex or hoverKey
        setHoverKey(key);
        setXHoverIndex(newXHoverIndex);
        onMouseOverHandler(e, key, newXHoverIndex); //run the callback from props
      }
    }
  };
  var mouseLeave = function () {
    setXHoverIndex(null);
    onMouseOverHandler(null); //run the callback from props
  };
  var getTitle = useCallback(function (key, dateFormat) {
    var title = key;
    if (xHoverIndex && data[xHoverIndex]) {
      title += ": (" + dateFormat(data[xHoverIndex].date) + ", " + data[xHoverIndex][key] + ")";
    }
    return title;
  }, [data, xHoverIndex]);
  var colorFunction = propsColorFunction || scaleOrdinal().domain(keys).range(schemeCategory10);
  var stack$1 = useMemo(function () {
    return stack().keys(keys).order(stackOrderNone).offset(stackOffsetNone);
  }, [keys]);
  var series = useMemo(
  //@ts-ignore
  function () {
    return stack$1(data);
  }, [data, stack$1]);
  var xScale = useMemo(function () {
    return scaleUtc()
    //@ts-ignore
    .domain(extent(data, function (d) {
      return d.date;
    })).range([margin.left, width - margin.right]);
  }, [data, margin, width]);
  var _d = useMemo(function () {
      return getYAxisData(data, keys, height, margin, yMaxTicks);
    }, [data, height, keys, margin, yMaxTicks]),
    yTicks = _d.yTicks,
    yScale = _d.yScale;
  var rectWidth = useMemo(function () {
    return Math.min(500, (width - margin.left - margin.right) / 2);
  }, [margin, width]);
  var area$1 = useMemo(function () {
    return area()
    //@ts-ignore
    .x(function (d) {
      return xScale(d.data.date);
    }).y0(function (d) {
      return yScale(d[0]);
    }).y1(function (d) {
      return yScale(d[1]);
    });
  }, [xScale, yScale]);
  return React.createElement("div", {
    ref: ref
  }, React.createElement("svg", {
    width: width,
    height: height,
    onMouseLeave: mouseLeave
  }, React.createElement("g", null, data.map(function (d, i) {
    if (i % xTicksSkip === 0) {
      //if we should render this x tick
      var xPixel = xScale(d.date); //get the x pixel value
      return React.createElement("g", {
        key: i
      }, React.createElement("line", {
        x1: xPixel,
        x2: xPixel,
        y1: height - margin.bottom + xTickSize,
        y2: margin.top,
        strokeWidth: axisStrokeWidth,
        stroke: axisStroke
      }), React.createElement("text", {
        x: xPixel,
        y: height - margin.bottom + xTickSize,
        textAnchor: "middle",
        dy: "1.25em"
      }, dateFormat(d.date)));
    }
    return null;
  })), React.createElement("g", null, yTicks.map(function (yTick, i) {
    var yPixel = yScale(yTick); //get the y pixel value
    return React.createElement("g", {
      key: i
    }, React.createElement("line", {
      key: i,
      x1: margin.left - yTickSize,
      x2: width - margin.right,
      y1: yPixel,
      y2: yPixel,
      strokeWidth: axisStrokeWidth,
      stroke: axisStroke
    }), React.createElement("text", {
      x: margin.left - yTickSize,
      y: yPixel,
      textAnchor: "end",
      dx: "-5px",
      dy: "0.35em"
    }, yFormat(yTick)));
  })), React.createElement("g", null, series.map(function (d, i) {
    if (d[0]) {
      //if there is a data point to show
      var sharedProps = {
        key: i,
        fill: colorFunction(d.key),
        opacity: areaOpacity,
        onClick: function (e) {
          return onClickHandler(e, d.key, xHoverIndex);
        },
        onTouchStart: function (e) {
          return onClickHandler(e, d.key, xHoverIndex);
        },
        onTouchMove: function (e) {
          return mouseMove(e, d.key);
        },
        onMouseMove: function (e) {
          return mouseMove(e, d.key);
        }
      };
      var title = React.createElement("title", null, getTitle(d.key, dateFormat));
      //if there are multiple data points, render a path
      if (data.length > 1) {
        return React.createElement("path", __assign({}, sharedProps, {
          d: area$1(d)
        }), title);
      }
      //else render a stacked bar chart
      var y1 = yScale(d[0][1]);
      return React.createElement("rect", __assign({}, sharedProps, {
        height: yScale(d[0][0]) - y1,
        width: rectWidth,
        x: xScale(d[0].data.date) - rectWidth / 2,
        y: y1
      }), title);
    }
    return null;
  })), xHoverIndex && data[xHoverIndex] && dots && function () {
    var xPixel = xScale(data[xHoverIndex].date);
    return React.createElement("g", null, React.createElement("line", {
      x1: xPixel,
      x2: xPixel,
      y1: margin.top,
      y2: height - margin.bottom,
      stroke: dotsStroke,
      strokeWidth: dotsStrokeWidth
    }), keys.map(function (k, i) {
      return React.createElement("circle", {
        key: i,
        cx: xPixel,
        cy: yScale(series[i][xHoverIndex][1]),
        r: dotsRadius,
        fill: colorFunction(k),
        stroke: dotsStroke,
        strokeWidth: dotsStrokeWidth,
        filter: dotsFilter
      }, React.createElement("title", null, getTitle(k, dateFormat)));
    }));
  }()));
}
//@ts-ignore

//@ts-ignore
StackedAreaChart.defaultProps = {
  areaOpacity: "0.9",
  axisStroke: "#ccc",
  axisStrokeWidth: 2,
  dateFormat: timeFormat("%Y"),
  dots: true,
  dotsFilter: "brightness(1.25)",
  dotsRadius: 10,
  dotsStroke: "#aaa",
  dotsStrokeWidth: 2,
  height: 500,
  margin: {
    top: 20,
    right: 30,
    bottom: 30,
    left: 60
  },
  onClickHandler: function (e, key, dataIndex) {},
  onMouseOverHandler: function (e, key, dataIndex) {},
  xTickSize: 5,
  xTicksSkip: 1,
  yFormat: function (y) {
    return y;
  },
  yMaxTicks: 5,
  yTickSize: 5
};
export default StackedAreaChart;